import { useState, useEffect } from "react";
import { SAME_LAYOUT_FOLD_THRESHOLD } from "@capture/shared";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  getClassifyDetail,
  classifyDocument,
  advancePartial,
  assignPageIdentity,
  reorderPages,
  splitDocument,
  movePage as movePageApi,
  mergeDocuments as mergeDocumentsApi,
  deletePage,
  deleteDocumentApi,
  attachPage,
  attachAllPages,
  rejectBatch,
  setCsResidency,
  flagForDeletion,
  getLayoutVariantsForForm,
  PARK_REASON_GUIDANCE,
} from "../api/client";
import type { CandidateForm, ClassifyDetail, ClassifyPage, PageAssignment } from "../api/client";
import { comingLabel, pageIdentityLine } from "./classify-detail-helpers";
import { SortableThumb } from "./SortableThumb";
import { SplitPanel } from "./SplitPanel";
import { MoveMergeControl } from "./MoveMergeControl";
import { NeighborStrip } from "./NeighborStrip";
import { useParkedDocAction } from "./useParkedDocAction";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { ClassifyResolver } from "./ClassifyResolver";
import { ResidencyFooter } from "./ResidencyFooter";
import { toast } from "@/components/Toaster";
import styles from "./ClassifyDetailPanel.module.css";

/**
 * R-255 Phase 5d-1 — ClassifyDetailPanel
 *
 * Detail view for a parked document at the classify station: park-reason
 * banner · sortable page filmstrip · image viewer · per-page identity editor ·
 * the live resolution actions (form re-pick / accept-partial / split / move /
 * merge) or a disabled "coming" affordance.
 *
 * Orchestrator only — split mode (SplitPanel), filmstrip thumbs (SortableThumb),
 * sibling move/merge (MoveMergeControl), and the busy/error action envelope
 * (useParkedDocAction) live in their own modules (R-260 CS-station cleanup).
 */

// Task 11 — mirrors LAYOUT_VARIANT_LIMITS.maxActivePerForm in
// apps/api/src/services/layout-variants.ts (spec §6.3, not tuned without
// amending the spec). Not exported from @capture/shared today — only
// SAME_LAYOUT_FOLD_THRESHOLD is — so this is derived from the active-variant
// count the GET already returns rather than re-declaring the cap value here.
const MAX_ACTIVE_LAYOUT_VARIANTS = 8;

interface ClassifyDetailPanelProps {
  documentId: string;
  onResolved: () => void;
}

export function ClassifyDetailPanel({
  documentId,
  onResolved,
}: ClassifyDetailPanelProps) {
  const [detail, setDetail] = useState<ClassifyDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedPageNumber, setSelectedPageNumber] = useState<number>(1);
  const [order, setOrder] = useState<string[]>([]); // local page-id order for the filmstrip
  const [splitMode, setSplitMode] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [mergeSource, setMergeSource] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<"page" | "doc" | null>(null);

  // Task 11 — "New layout of this form" checkbox state. Tracks whichever
  // candidate form ClassifyResolver's picker currently has selected (reported
  // via onSelectedFormChange) so the gate below can be looked up for it.
  const [selectedCandidateForm, setSelectedCandidateForm] = useState<CandidateForm | null>(null);
  const [declareNewLayout, setDeclareNewLayout] = useState(false);
  const [layoutVariantGate, setLayoutVariantGate] = useState<{
    canDeclare: {
      allowed: boolean;
      reason: string | null;
      /** E-059 — structural refusal discriminator from the gate response. */
      code?: "not_found" | "not_enabled" | "zoned" | "at_cap" | null;
    };
    activeVariantCount: number;
  } | null>(null);

  // Shared busy/error slot + the terminal / advance-or-park action envelopes.
  // onRefetch re-syncs both the detail and the filmstrip order so every parked
  // (non-advancing) action lands the panel on the freshly persisted page-state.
  const { busy, actionError, setActionError, runTerminal, runParked } =
    useParkedDocAction({
      documentId,
      onResolved,
      onRefetch: (fresh) => {
        setDetail(fresh);
        setOrder(fresh.pages.map((p) => p.id));
      },
    });

  // ── dnd-kit sensors — MUST be at top level (hooks rule) ──────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    setDetail(null);
    setActionError(null);
    setOrder([]);
    setSplitMode(false);
    setMoveTarget("");
    setMergeSource("");
    setDeleteTarget(null);

    getClassifyDetail(documentId)
      .then((d) => {
        setDetail(d);
        setOrder(d.pages.map((p) => p.id));
        // Default to the first page's pageNumber
        const firstPage = d.pages[0];
        setSelectedPageNumber(firstPage ? firstPage.pageNumber : 1);
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : "Failed to load document.");
      })
      .finally(() => setIsLoading(false));
  }, [documentId, setActionError]);

  // Task 11 — refetch the layout-variant gate whenever the operator's
  // candidate-form selection changes. A stale tick from a PRIOR form must
  // never carry over to a newly-selected one, so declareNewLayout resets here
  // too. `cancelled` guards against a slow response landing after the
  // selection (or the document) has already moved on.
  useEffect(() => {
    setDeclareNewLayout(false);
    if (!selectedCandidateForm?.jobTypeId) {
      setLayoutVariantGate(null);
      return;
    }
    let cancelled = false;
    getLayoutVariantsForForm(selectedCandidateForm.jobTypeId, selectedCandidateForm.id)
      .then((res) => {
        if (cancelled) return;
        setLayoutVariantGate({
          canDeclare: res.canDeclare,
          activeVariantCount: res.variants.filter((v) => v.status === "active").length,
        });
      })
      .catch(() => {
        if (!cancelled) setLayoutVariantGate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCandidateForm?.id, selectedCandidateForm?.jobTypeId]);

  // ── Loading / error ─────────────────────────────────────────────────────────

  if (isLoading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  if (loadError || !detail) {
    return <div className={styles.errorBanner}>{loadError ?? "No data."}</div>;
  }

  // ── Derived values ──────────────────────────────────────────────────────────

  const guidance =
    PARK_REASON_GUIDANCE[detail.parkReason] ?? PARK_REASON_GUIDANCE.unknown!;

  const selectedPage =
    detail.pages.find((p) => p.pageNumber === selectedPageNumber) ??
    detail.pages[0];

  const hasSiblings = detail.siblings.length > 0;

  // Two pages sharing a page-type signal the doc likely holds two documents.
  const seenPageTypes = new Set<string>();
  const duplicateType = detail.pages.some((p) => {
    if (p.pageTypeId == null) return false;
    if (seenPageTypes.has(p.pageTypeId)) return true;
    seenPageTypes.add(p.pageTypeId);
    return false;
  });

  // Task 11 — "New layout of this form" checkbox visibility.
  //
  // A doc parked at classify already has a real classConfidence whenever
  // fingerprinting resolved SOME form for it (e.g. low_confidence — matched,
  // just not confidently enough to auto-extract). That score clearing
  // SAME_LAYOUT_FOLD_THRESHOLD means the shape was already recognised as an
  // existing layout — offering to declare a NEW one would be actively wrong.
  // Below the band (or null, e.g. no_form) means genuinely unrecognised.
  const matchedExistingLayout =
    detail.classConfidence != null && detail.classConfidence >= SAME_LAYOUT_FOLD_THRESHOLD;

  const activeVariantCount = layoutVariantGate?.activeVariantCount ?? 0;
  // canDeclare.allowed is false for "not enabled", "zoned", AND "at the cap".
  // E-059 — the cap AND the zoned refusal surface a DISABLED checkbox with
  // the reason (an operator must be able to distinguish "not available for
  // this form" from "no such feature exists"); only "not enabled" hides the
  // affordance entirely (feature off = nothing to explain at the station).
  // The cap is distinguished by the active-variant count the same response
  // carries; zoned by the structural canDeclare.code — never by parsing the
  // reason string.
  const atLayoutVariantCap =
    layoutVariantGate != null &&
    !layoutVariantGate.canDeclare.allowed &&
    activeVariantCount >= MAX_ACTIVE_LAYOUT_VARIANTS;
  const zonedLayoutVariantRefusal =
    layoutVariantGate != null &&
    !layoutVariantGate.canDeclare.allowed &&
    layoutVariantGate.canDeclare.code === "zoned";
  const layoutVariantRefusalShown = atLayoutVariantCap || zonedLayoutVariantRefusal;
  const showLayoutVariantCheckbox =
    !matchedExistingLayout &&
    layoutVariantGate != null &&
    (layoutVariantGate.canDeclare.allowed || layoutVariantRefusalShown);

  // Filmstrip ordering
  const pageById = new Map(detail.pages.map((p) => [p.id, p]));
  const orderedPages = order
    .map((id) => pageById.get(id))
    .filter((p): p is ClassifyPage => p != null);
  const orderChanged =
    order.length === detail.pages.length &&
    order.some((id, i) => id !== detail.pages[i]?.id);

  // ── Filmstrip drag + move helpers ────────────────────────────────────────────

  // Move within the local order, no-op if either index is out of range.
  function reorder(cur: string[], from: number, to: number) {
    return from === -1 || to < 0 || to >= cur.length ? cur : arrayMove(cur, from, to);
  }

  function handleDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    setOrder((cur) =>
      reorder(cur, cur.indexOf(String(e.active.id)), cur.indexOf(String(e.over!.id))),
    );
  }

  function movePage(id: string, dir: -1 | 1) {
    setOrder((cur) => {
      const from = cur.indexOf(id);
      return reorder(cur, from, from + dir);
    });
  }

  // ── Action handlers — thin wrappers over the shared envelope ──────────────────

  const handleApplyOrder = () => {
    // F-058 — page reorder requires a resolved form server-side (else a raw 409
    // "Document has no form" leaked to the screen). Guard with a friendly,
    // actionable message instead; the button is also disabled in this state.
    if (!detail.form) {
      setActionError("Resolve this document's form first — reordering needs an identified form.");
      return;
    }
    void runParked(() => reorderPages(detail.id, order), "Reorder failed.");
  };

  const handleClassify = (formId: string) => {
    void runTerminal(async () => {
      // Omit the 3rd arg entirely when unticked (rather than passing `false`)
      // so the byte-identical call shape of every pre-existing classify path
      // is preserved verbatim — this is the common case.
      const res = declareNewLayout
        ? await classifyDocument(detail.id, formId, true)
        : await classifyDocument(detail.id, formId);
      // F-064 — a cross-type route silently moved the doc to another job's
      // batch; name the destination so the operator can follow it.
      if (res.routed) {
        toast.success(
          // E-050 — lead with the batch identity (label), then name, then id.
          `Routed to ${res.routed.jobTypeName ?? "another job"} — batch ${res.routed.batchLabel ?? res.routed.batchName ?? res.routed.batchId}`,
        );
      }
    }, "Classification failed.");
  };

  const handleAdvance = () => {
    void runTerminal(() => advancePartial(detail.id), "Failed to advance.");
  };

  const handleAssign = (pageId: string, assignment: PageAssignment) => {
    void runParked(
      () => assignPageIdentity(detail.id, pageId, assignment),
      "Assignment failed.",
    );
  };

  const handleApplySplit = (boundaryIds: string[]) => {
    void runTerminal(() => splitDocument(detail.id, boundaryIds), "Split failed.");
  };

  const handleMovePage = () => {
    if (!selectedPage || !moveTarget) return;
    void runParked(
      () => movePageApi(detail.id, selectedPage.id, moveTarget),
      "Move failed.",
      () => setMoveTarget(""),
    );
  };

  const handleMerge = () => {
    if (!mergeSource) return;
    void runParked(
      () => mergeDocumentsApi(detail.id, mergeSource),
      "Merge failed.",
      () => setMergeSource(""),
    );
  };

  const handleDeletePage = (rescan: { note?: string } | undefined) => {
    if (!selectedPage) return;
    void runParked(
      () =>
        deletePage(detail.id, selectedPage.id, rescan).then((r) => ({
          advanced: r.advanced || r.documentDeleted, // emptied doc leaves the queue too
        })),
      "Delete failed.",
      () => setDeleteTarget(null),
    );
  };

  const handleDeleteDoc = (rescan: { note?: string } | undefined) => {
    void runTerminal(() => deleteDocumentApi(detail.id, rescan), "Delete failed.");
  };

  // R-262 5d-5b — quiet-attach: send the current page (or all pages) to an
  // advanced batch-mate without touching its step/status/lock/assignment.
  const handleAttachPage = (targetId: string) => {
    if (!selectedPage) return;
    void runParked(() => attachPage(detail.id, selectedPage.id, targetId), "Attach failed.");
  };

  const handleAttachAll = (targetId: string) => {
    void runTerminal(() => attachAllPages(detail.id, targetId), "Attach failed.");
  };

  // R-266 6a — reject_batch disposition: a confirmed intruder poisons the whole
  // exact-composition batch. Terminal action (the batch dissolves and this doc
  // leaves the queue). Window-confirm gate is sufficient for 6a.
  const handleRejectBatch = () => {
    if (
      !window.confirm(
        "Reject the entire batch? Every document in it is voided and re-scan requested. This cannot be undone.",
      )
    ) {
      return;
    }
    void runTerminal(() => rejectBatch(detail.batchId), "Reject batch failed.");
  };

  // CS-2 — ResidencyFooter handlers. All use runParked so a non-advancing action
  // re-fetches the detail (the residency state updates in place; doc stays parked).
  // flagForDeletion with deleted:true is treated as terminal (doc leaves queue).
  const handleResearchHold = (note?: string) => {
    void runParked(
      () => setCsResidency(detail.id, "research_hold", note).then(() => ({ advanced: false })),
      "Research hold failed.",
    );
  };

  const handleClearResidency = () => {
    void runParked(
      () => setCsResidency(detail.id, "clear").then(() => ({ advanced: false })),
      "Clear residency failed.",
    );
  };

  const handleFlagDeletion = (note?: string) => {
    void runParked(
      () =>
        flagForDeletion(detail.id, note).then((res) => ({
          // deleted:true means the doc is gone → treat as advanced to close the panel
          advanced: res.deleted,
        })),
      "Flag for deletion failed.",
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.panel}>
      {/* Banner */}
      <div className={styles.banner}>
        <span className={styles.bannerLabel}>{guidance.label}</span>
        <p className={styles.bannerGuidance}>{guidance.guidance}</p>
      </div>

      {duplicateType && (
        <div className={styles.splitHint}>
          Two pages share a type — split likely. Use “Split document” below.
        </div>
      )}

      {splitMode ? (
        <SplitPanel
          detail={detail}
          busy={busy}
          actionError={actionError}
          onApplySplit={handleApplySplit}
          onCancel={() => {
            setSplitMode(false);
            setActionError(null);
          }}
        />
      ) : (
        <>
          {/* R-262 5d-5b — scan-order neighbor strip (prev / next batch-mates) */}
          <NeighborStrip
            prev={detail.neighbors.prev}
            next={detail.neighbors.next}
            busy={busy}
            onAttachPage={handleAttachPage}
            onAttachAll={handleAttachAll}
          />

          {/* Filmstrip + Viewer */}
          <div className={styles.content}>
            {/* Filmstrip */}
            <div className={styles.filmstrip}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={order}
                  strategy={horizontalListSortingStrategy}
                >
                  {orderedPages.map((page, i) => (
                    <SortableThumb
                      key={page.id}
                      page={page}
                      index={i}
                      count={orderedPages.length}
                      selected={page.pageNumber === selectedPageNumber}
                      onSelect={() => setSelectedPageNumber(page.pageNumber)}
                      onMove={(dir) => movePage(page.id, dir)}
                      disabled={busy}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {detail.pages.length >= 2 && (
                <button
                  type="button"
                  className={styles.btn}
                  disabled={!orderChanged || busy || !detail.form}
                  title={
                    !detail.form
                      ? "Resolve this document's form first — reordering needs an identified form."
                      : undefined
                  }
                  onClick={handleApplyOrder}
                >
                  Apply order
                </button>
              )}
            </div>

            {/* Viewer */}
            <div className={styles.viewer}>
              <div className={styles.viewerImageWrap}>
                <img
                  key={selectedPageNumber}
                  src={`/api/documents/${detail.id}/pages/${selectedPageNumber}/image`}
                  alt={`Page ${selectedPageNumber}`}
                  className={styles.viewerImg}
                />
              </div>
              {selectedPage && (
                <div className={styles.viewerIdentity}>
                  {pageIdentityLine(selectedPage)}
                </div>
              )}
              {hasSiblings && selectedPage && (
                <div className={styles.moveMergeRow}>
                  <MoveMergeControl
                    variant="move"
                    siblings={detail.siblings}
                    value={moveTarget}
                    busy={busy}
                    onChange={setMoveTarget}
                    onSubmit={handleMovePage}
                  />
                </div>
              )}
              {selectedPage && (
                <button
                  type="button"
                  className={styles.dangerBtn}
                  disabled={busy}
                  onClick={() => setDeleteTarget("page")}
                >
                  Delete page
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            {detail.pages.length >= 2 && (
              <button
                type="button"
                className={styles.btn}
                disabled={busy}
                onClick={() => setSplitMode(true)}
              >
                Split document
              </button>
            )}

            {hasSiblings && (
              <MoveMergeControl
                variant="merge"
                siblings={detail.siblings}
                value={mergeSource}
                busy={busy}
                onChange={setMergeSource}
                onSubmit={handleMerge}
              />
            )}

            {/* CS-2 — ClassifyResolver replaces the inline form + page-type
                pickers. Rendered when the guidance calls for form selection
                (Type + Pages cascade) or when the doc is editable with a
                resolved form (Pages cascade only — Type is always shown by
                ClassifyResolver, which is acceptable for re-classification). */}
            {(guidance.liveAction === "form" ||
              (Boolean(guidance.editable) && detail.form != null)) && (
              <>
                <ClassifyResolver
                  detail={detail}
                  onClassify={handleClassify}
                  onAssign={handleAssign}
                  onSelectedFormChange={setSelectedCandidateForm}
                />
                {showLayoutVariantCheckbox && (
                  <div className={styles.layoutVariantRow}>
                    <label className={styles.layoutVariantCheck}>
                      <input
                        type="checkbox"
                        data-testid="cs-new-layout-checkbox"
                        checked={declareNewLayout}
                        disabled={layoutVariantRefusalShown}
                        onChange={(e) => setDeclareNewLayout(e.target.checked)}
                      />
                      New layout of this form
                    </label>
                    <p className={styles.layoutVariantHelper}>
                      {layoutVariantRefusalShown
                        ? layoutVariantGate?.canDeclare.reason
                        : "Tick only if this is the same form in a different layout — not a poor scan."}
                    </p>
                  </div>
                )}
              </>
            )}

            {guidance.liveAction === "advance" && (
              <button
                type="button"
                className={styles.btn}
                disabled={busy}
                onClick={handleAdvance}
              >
                Accept partial
              </button>
            )}

            {/* No "coming" placeholder when a live affordance already resolves
                the park reason (e.g. misordered → filmstrip Apply order). */}
            {guidance.liveAction === null && !guidance.resolvedLive && (
              <button type="button" className={styles.btn} disabled>
                {comingLabel(detail.parkReason)}
              </button>
            )}

            {/* R-266 6a — reject_batch disposition only: a confirmed cross-type
                intruder dissolves the whole exact-composition batch. */}
            {detail.crossTypeRouting === "reject_batch" && (
              <button
                type="button"
                className={styles.dangerBtn}
                disabled={busy}
                onClick={handleRejectBatch}
              >
                Reject batch
              </button>
            )}

            {actionError && <p className={styles.actionError}>{actionError}</p>}

            {/* CS-2 — ResidencyFooter: research hold / flag-for-deletion / delete / clear.
                onDeleteDocument opens the existing ConfirmDeleteDialog (with rescan support),
                which calls handleDeleteDoc → deleteDocumentApi. This is the SINGLE entry
                point for document deletion — the former standalone panel button is removed. */}
            <ResidencyFooter
              detail={detail}
              onResearchHold={handleResearchHold}
              onFlagDeletion={handleFlagDeletion}
              onDeleteDocument={() => setDeleteTarget("doc")}
              onClearResidency={handleClearResidency}
            />
          </div>
        </>
      )}

      {deleteTarget === "page" && selectedPage && (
        <ConfirmDeleteDialog
          title={`Delete page ${selectedPage.pageNumber}?`}
          message="The page image is removed permanently. The document re-assembles from the remaining pages."
          busy={busy}
          onConfirm={(rescan) => handleDeletePage(rescan)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {deleteTarget === "doc" && (
        <ConfirmDeleteDialog
          title="Delete document?"
          message="All pages and images are removed permanently. The document is voided with an audit record."
          busy={busy}
          onConfirm={(rescan) => handleDeleteDoc(rescan)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
